import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { OUT_OF_SESSION } from '../../../constants';
import { CheckResponseTypes, EventTypes } from '../../../constants/event';
import { selectMenuVersion } from '../../../reducers/menuSlice';
import {
  CheckTransmissionMessage,
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import { IOrderTransmissionMessage } from '../../../reducers/orderSlice.props';
import {
  selectRestaurant,
  selectStage,
} from '../../../reducers/restaurantSlice';
import logger, { setLogSessionId } from '../../../utils/logger';
import {
  SessionEndReasons,
  SessionStartReasons,
  SessionStatus,
} from './sessionBoundary.constants';
import {
  checkSessionEnd,
  fillEndSessionValues,
  initialState,
} from './sessionBoundary.utils';

/* HITL session ends when:
1. Session logout button is clicked 
Or
2. Three conditions are satisfied: car_exit event detected, empty cart and no pending final order. The sequence of satisfying these conditions can be different.
    No pending final order means: 
    1) no check is ever created on the POS within the session 
    2) the check is created but the final order is submitted by clicking Finish Order button 
    3) the check is created but canceled by clicking Cancel Order button
    4) the check is created but canceled by asking for restaurant staff intervention or receiving restaurant staff intervention
*/

const sessionBoundarySlice = createSlice({
  name: 'sessionBoundary',
  initialState,
  reducers: {
    setFinalOrderSubmitted: (state, action: PayloadAction<boolean>) => {
      // The action is called by clicking Finish Order or Cancel Order and FinalOrderBackdrop handlers
      state.finalOrderSubmitted = action.payload;
      if (checkSessionEnd(state))
        fillEndSessionValues(state, SessionEndReasons.finalOrderSubmitted);
    },
    resetSessionBoundaryData: () => {
      logger.info('Reset session boundary states');
      setLogSessionId('');
      return { ...initialState };
    },
    resetSBOrderStates: (state) => {
      logger.info('Reset order states in session boundary');
      state.orderMessageOrderId = '';
      state.finalOrderSubmitted = false;
      state.checkCreated = false;
    },
    setSessionStart: (
      state,
      action: PayloadAction<{
        sessionId: string;
        sessionStartReason: SessionStartReasons;
      }>
    ) => {
      // Session starts
      const { sessionId, sessionStartReason } = action.payload;
      logger.info(
        `Start HITL session with sessionId ${sessionId} and sessionStartReason ${sessionStartReason}`
      );
      state.sessionId = sessionId;
      setLogSessionId(sessionId);
      state.sessionStatus = SessionStatus.start;
      state.sessionStartReason = sessionStartReason;
      state.sessionEndReason = '';
      state.endedSessionId = '';
    },
    setCartEmpty: (state, action: PayloadAction<boolean>) => {
      state.cartEmpty = action.payload;
      if (checkSessionEnd(state))
        fillEndSessionValues(state, SessionEndReasons.cartEmpty); // Session ends: the final order is submitted/canceled after a car exits
    },
    endSession: (state, action: PayloadAction<SessionEndReasons>) => {
      fillEndSessionValues(state, action.payload);
    },
    setSessionTimeoutDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSessionTimeoutDialogOpen = action.payload;
    },
    setShowOrderSubmissionAlert: (state, action: PayloadAction<boolean>) => {
      state.showOrderSubmissionAlert = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const {
          messages,
          FEATURE_FLAG_FINAL_ORDER_VALIDATION = false,
          ai,
        } = action.payload;
        messages.forEach((message) => {
          const { session_id: messageSessionId, event } = message;
          switch (event) {
            case EventTypes.carEnter:
              if (!state.sessionId) {
                // Session starts
                logger.info({
                  message: `Start session boundary session with session id ${messageSessionId} on car_enter`,
                  moreInfo: JSON.stringify(message),
                });
                state.sessionId = messageSessionId;
                setLogSessionId(messageSessionId);
                state.sessionStatus = SessionStatus.start;
                state.sessionStartReason = SessionStartReasons.carEnter;
                state.sessionEndReason = '';
                state.endedSessionId = '';
              }
              break;
            case EventTypes.carExit:
              if (
                [state.sessionId, OUT_OF_SESSION].includes(messageSessionId)
              ) {
                state.carExit = true;
                if (checkSessionEnd(state)) {
                  // Session ends: a car exits with no pending submitted order
                  fillEndSessionValues(state, SessionEndReasons.carExit);
                } else {
                  logger.info(
                    `Post car exit timer activated on car_exit for session id ${messageSessionId}`
                  );
                  state.isPostCarExitTimerActivated = true;
                }
              }
              break;
            case EventTypes.check:
              // TODO: need to add messageSessionId === state.sessionId after POSLink stops using the orderId from order event as the sessionId of check event
              const { check, final } = (message as CheckTransmissionMessage)
                .data;
              if (check && !state.checkCreated) state.checkCreated = true;

              if (!final) return;

              if (!FEATURE_FLAG_FINAL_ORDER_VALIDATION || ai?.isAIAutoMode) {
                logger.info(
                  `finalOrderSubmitted is set to true when final check is received: ${messageSessionId}`
                );
                state.finalOrderSubmitted = true;
              }

              if (ai?.isAIAutoMode) {
                // Final order is submitted successfully by AI.
                logger.info(
                  `showOrderSubmissionAlert is set to true when final check is submitted by AI: ${messageSessionId}`
                );
                state.showOrderSubmissionAlert = true;
              }

              if (checkSessionEnd(state)) {
                // Session ends: the final order is submitted after a car exits
                fillEndSessionValues(
                  state,
                  SessionEndReasons.finalOrderSubmitted
                );
              }
              break;
            case EventTypes.order:
              if (messageSessionId === state.sessionId) {
                const orderMessageData = (message as IOrderTransmissionMessage)
                  .data || { session_id: '', final: false };
                state.orderMessageData = orderMessageData;
                const { session_id, final } = orderMessageData;
                logger.info({
                  message: `Set orderMessageOrderId to ${session_id} on order event`,
                  moreInfo: JSON.stringify(message),
                });
                state.orderMessageOrderId = session_id; // Order from other components. Only support single instance for now as AI does not support multiple orders per session.
              }
              break;
            default:
              break;
          }
        });
      }
    );
    builder.addMatcher(
      isAnyOf(
        selectRestaurant.fulfilled,
        selectStage.fulfilled,
        selectMenuVersion.fulfilled
      ),
      () => {
        setLogSessionId('');
        return initialState;
      }
    );
  },
});

const {
  setFinalOrderSubmitted,
  resetSessionBoundaryData,
  resetSBOrderStates,
  setSessionStart,
  setCartEmpty,
  endSession,
  setSessionTimeoutDialogOpen,
  setShowOrderSubmissionAlert,
} = sessionBoundarySlice.actions;
export {
  endSession,
  resetSBOrderStates,
  resetSessionBoundaryData,
  setCartEmpty,
  setFinalOrderSubmitted,
  setSessionStart,
  setSessionTimeoutDialogOpen,
  setShowOrderSubmissionAlert,
};

export default sessionBoundarySlice.reducer;
